// The sky, from the scattering tables.
//
// One screen-filling quad, drawn before everything but the stars, with the view
// ray reconstructed per fragment and the sky-view table sampled along it. There
// is no dome and no band switch: the same fragment answers every altitude,
// because the only thing that changes with altitude is where the ray starts.
//
//   inside the shell   the camera's own point, and the table was built at the
//                      camera's own height, so it is one texture fetch
//   above the shell    the ray's entry point into the shell, which is the ORBIT
//                      limb: a ray that misses the shell scatters nothing and
//                      the frame keeps its stars
//
// Which is why there is no discontinuity to cross - `descent-dojo`'s pop
// criterion is what says so. Terrain draws over it by depth, so what is left is
// whatever the terrain did not cover.
//
// The alpha channel is not a fade. It is one minus the transmittance along the
// ray, so the air takes out of the stars and the pane's clear colour exactly
// what it absorbs, and the blend is `radiance + (1 - alpha) * behind`.
//
// What the ORBIT limb does NOT have is day/night asymmetry. Above the shell
// this fragment recomputes the view direction and the sun's azimuth at the
// ray's ENTRY point, but the table itself was built for the sun altitude at the
// CAMERA's own sub-point (`atmoSunDirLocal` in src/atlas.ts), and the table
// carries no muSun axis to index instead. So with the camera over the
// terminator the limb above the night side reads the same radiance as the limb
// above the day side. Indexing the sky-view table on muSun as a third axis is
// the Phase 4 item that fixes it (docs/roadmap/05-atmosphere-and-light.md);
// nothing measures it today, since `descent-dojo --sky` reads CLAIM, PLOT and
// SURFACE, all of them inside the shell.

import * as THREE from 'three';

import type { AtmosphereHandle } from './atmosphere';
import { ATLAS_CLEAR_HEX } from './mount.ts';

/**
 * Renderer units per W/m2/sr, before ACES.
 *
 * The tables hold radiance per unit sun illuminance, so a texel times the
 * body's own top-of-atmosphere irradiance is a real radiance - tens of W/m2/sr
 * in a Mars noon sky - and something has to carry it into the range the tone
 * curve in `src/main.ts` was set for. This is that something, and it stays
 * separate from `BAND_EXPOSURE` in `src/atlas.ts` rather than folding into it:
 * that table is the CAMERA's exposure, applied by the renderer to every lit
 * pixel at once, and this is a unit conversion on one consumer's own input.
 * Folded together, the sky would be the only thing exposed twice.
 *
 * Picked against `descent-dojo --sky`: it puts a noon sky band at SURFACE near
 * the 60 grey levels the authored dome it replaces read, and keeps the 7 degree
 * sun at the ends of the sol clear of the arm's 4.0 floor without ACES clipping
 * the noon sky to white.
 */
export const SKY_EXPOSURE = 0.0075;

/**
 * Drawn size the shell has to reach before the sky is worth a pass.
 *
 * 0.05 degrees is one pixel at `07-performance-and-instruments.md`'s reference
 * viewport (900 rendered rows over a 45 degree field). Below it the limb is
 * under a pixel, so switching the quad off there cannot be a pop - which the
 * orrery's own `MIN_DRAWN_ANGULAR_DEG` of 0.15 does not settle, because that
 * one INFLATES a body to keep it clickable and this one is about what the sky
 * actually covers.
 */
export const SKY_MIN_ANGULAR_DIAMETER_DEG = 0.05;

/**
 * Where the shell sits in the order, between the system and its own body.
 *
 * The quad tests depth and writes none, so every globe it has to blend over -
 * which is every globe past the shell - must already be in the depth buffer
 * when it draws, at `BEHIND_AIR_ORDER` in src/atlas.ts, and the body the air
 * belongs to must draw after it: that ground carries the same air as an aerial
 * term of its own and a second wash of it would count the air twice.
 */
export const SKY_RENDER_ORDER = -2;

/**
 * The order the shell had before it took part in depth at all.
 *
 * `descent-dojo --airorder --inject legacy-sky`: drawn before everything with
 * no depth test, which is what put a body 1.2 million km away over the haze in
 * front of it.
 */
export const SKY_LEGACY_RENDER_ORDER = -99999;

/**
 * Where, in the tone curve's own output level, the sky seen from under the
 * air starts and finishes taking its radiance's hue rather than the curve's.
 *
 * ACES runs each channel into its shoulder on its own, so a bright colour
 * comes out nearer white the brighter it is: the glow round a sun 5 degrees
 * up, whose radiance is 0.76 green and 0.35 blue to its red, drew 0.92 and
 * 0.70. Past the upper level the hue is the radiance's and the curve sets the
 * brightest channel alone, the way the ground view's sun disc is drawn; under
 * the lower one the sky is exactly what it was. From outside the shell the
 * limb keeps the curve throughout (`descent-dojo --sunset`).
 */
export const SKY_HUE_KEEP_LEVELS: readonly [number, number] = [0.4, 0.8];

/**
 * Ceiling on the sky from under the air, sRGB output levels, with the pane's
 * own clear colour it blends over counted in. See docs/ATLAS-TERRAIN.md.
 */
export const SKY_PEAK_SRGB = 250 / 255;

const VERT = /* glsl */ `
uniform mat4 uSkyInvProj;
uniform mat3 uSkyCamRot;
varying vec3 vSkyRay;
void main() {
  // The quad is in clip space already, so the only work is the ray: unproject
  // the near plane and rotate it out of view space by the camera's own basis.
  vec4 v = uSkyInvProj * vec4(position.xy, -1.0, 1.0);
  vSkyRay = uSkyCamRot * (v.xyz / v.w);
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const FRAG = /* glsl */ `
precision highp float;

/** Far enough that the deck's own span is what clips the ray and not this. */
#define SKY_DECK_FAR_M 1.0e9
#define SKY_HUE_KEEP_FROM ${SKY_HUE_KEEP_LEVELS[0].toFixed(3)}
#define SKY_HUE_KEEP_TO ${SKY_HUE_KEEP_LEVELS[1].toFixed(3)}
#define SKY_PEAK_SRGB ${SKY_PEAK_SRGB.toFixed(5)}

/** The clear colour behind the sky, sRGB-encoded, which is how the blend meets it. */
uniform vec3 uSkyBehind;

/** 0 under descent-dojo --sunset --inject white-glow, and never in the game. */
uniform float uSkyHueKeep;
/** The share of the shell drawn: under 1 only while cruise eases a body's own look in. */
uniform float uSkyFade;

/** Unit, RENDER frame: the local zenith at the camera's own ground point. */
uniform vec3 uSkyCamUp;
/** Ground radius plus the camera's altitude, metres. */
uniform float uSkyCamRadius;
/** Unit, RENDER frame: toward the sun. */
uniform vec3 uSkySunDir;
/** Top-of-atmosphere irradiance times SKY_EXPOSURE. */
uniform float uSkySunIrradiance;
/** Unit, RENDER frame: where the camera looks. */
uniform vec3 uSkyCamFwd;
/** The camera's own near and far planes, render units. */
uniform vec2 uSkyDepthRange;
#ifdef USE_LOGDEPTHBUF
uniform float logDepthBufFC;
#endif
varying vec3 vSkyRay;

// No _pars_ includes: three's own fragment prefix already carries the tone
// curve and the output transfer function for a ShaderMaterial, and including
// them again is a redefinition the shader will not link through.

void main() {
  vec3 dir = normalize(vSkyRay);
  vec3 up = uSkyCamUp;
  bool inShell = true;
  // Where the air in front of this fragment starts, metres. Zero under the
  // shell's own top, where the air starts at the eye.
  float entryM = 0.0;
  if (uSkyCamRadius > uAtmoRadii.y) {
    // Outside: the sky starts where the ray enters the shell, and the local
    // frame is the entry point's. A ray that misses it scatters nothing.
    vec3 c = up * uSkyCamRadius;
    float t = atmoRaySphere(c, dir, uAtmoRadii.y);
    if (t < 0.0) inShell = false;
    else {
      entryM = t;
      up = normalize(c + dir * t);
    }
  }
  vec3 radiance = vec3(0.0);
  float alpha = 0.0;
  if (inShell) {
    // Any tangent pair will do: the table is azimuthally symmetric about the
    // zenith and indexed by the view's azimuth RELATIVE to the sun's, so the
    // choice cancels between the two transforms below.
    vec3 seed = abs(up.y) > 0.9 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
    vec3 t1 = normalize(cross(up, seed));
    vec3 t2 = cross(up, t1);
    vec3 dl = vec3(dot(dir, t1), dot(dir, up), dot(dir, t2));
    vec3 sl = vec3(dot(uSkySunDir, t1), dot(uSkySunDir, up), dot(uSkySunDir, t2));
    radiance = atmoSkyRadiance(dl, sl) * uSkySunIrradiance * uAtmoWhite;
    vec3 tr = atmoViewTransmittance(dl);
    alpha = clamp(1.0 - (tr.r + tr.g + tr.b) / 3.0, 0.0, 1.0);
  }
  // The deck is in front of whatever the shell left behind it, from either
  // side: the limb ring over the globe's own silhouette from outside, and the
  // whole overcast ceiling from under the base, where the sun has no disc.
  // The frame it needs - one normalize, two crosses, six dots - is built only
  // when there is a deck to place in it: atmoDeck early-returns on 20 of the
  // 21 rows, and this used to pay for the frame before asking it to.
  float deckT = 1.0;
  vec3 deckS = vec3(0.0);
  if (uAtmoDeckExt > 0.0) {
    // The camera's OWN local frame, which is the deck's: up above may have
    // been moved to the ray's entry point into the shell, and the slab is a
    // fact about where the eye is rather than about where its ray crosses it.
    vec3 dseed = abs(uSkyCamUp.y) > 0.9 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
    vec3 dc1 = normalize(cross(uSkyCamUp, dseed));
    vec3 dc2 = cross(uSkyCamUp, dc1);
    deckS = atmoDeck(
      vec3(0.0, uSkyCamRadius, 0.0),
      vec3(dot(dir, dc1), dot(dir, uSkyCamUp), dot(dir, dc2)),
      vec3(dot(uSkySunDir, dc1), dot(uSkySunDir, uSkyCamUp), dot(uSkySunDir, dc2)),
      SKY_DECK_FAR_M, deckT);
  }
  radiance = radiance * deckT + deckS * uSkySunIrradiance;
  alpha = clamp(max(alpha, 1.0 - deckT), 0.0, 1.0);
  // The air is an occluder, not a backdrop: a globe past the shell is already
  // in the depth buffer when this quad draws, and this fragment stands where
  // its own ray enters the shell, so the test leaves a globe NEARER than that
  // alone and blends over everything behind it. The world frame is a metre per
  // unit (UNITS_PER_KM in src/atlas/orrery.ts), which is the frame the eye is
  // in whenever it is outside the shell; under the top entryM is zero, which is
  // the near plane, and the test passes the way it did before.
  float viewZ = max(entryM * dot(dir, uSkyCamFwd), 0.0);
  if (!inShell) viewZ = uSkyDepthRange.y;
  #ifdef USE_LOGDEPTHBUF
  gl_FragDepth = log2(1.0 + viewZ) * logDepthBufFC * 0.5;
  #else
  float dNear = uSkyDepthRange.x;
  float dFar = uSkyDepthRange.y;
  float ndcZ = (dFar + dNear) / (dFar - dNear)
    - (2.0 * dFar * dNear) / ((dFar - dNear) * max(viewZ, dNear));
  gl_FragDepth = clamp(ndcZ * 0.5 + 0.5, 0.0, 1.0);
  #endif
  gl_FragColor = vec4(radiance, alpha) * uSkyFade;
  #include <tonemapping_fragment>
  #ifdef TONE_MAPPING
  // From under the air the curve's shoulder takes a low sun's glow to white,
  // so there the hue is the radiance's own and the curve sets only the level.
  if (uSkyHueKeep > 0.5 && uSkyCamRadius <= uAtmoRadii.y) {
    float peak = max(max(radiance.r, radiance.g), radiance.b);
    if (peak > 0.0) {
      float level = toneMapping(vec3(peak)).g;
      float keep = smoothstep(SKY_HUE_KEEP_FROM, SKY_HUE_KEEP_TO, level);
      vec3 hue = radiance / peak;
      vec3 room = sRGBTransferEOTF(vec4(vec3(SKY_PEAK_SRGB) - (1.0 - alpha) * uSkyBehind, 1.0)).rgb / max(hue, vec3(1e-4));
      gl_FragColor.rgb = mix(gl_FragColor.rgb, hue * min(level, min(min(room.r, room.g), room.b)), keep);
    }
  }
  #endif
  #include <colorspace_fragment>
}
`;

export interface AtmoSkyHandle {
  readonly mesh: THREE.Mesh;
  /** `--inject legacy-sky`: the shell back in front of the order, out of depth. */
  setLegacyOrder(on: boolean): void;
  /** `descent-dojo --sunset --inject white-glow` passes false: the curve's own hue everywhere. */
  setHueKeep(on: boolean): void;
  /** The share of the shell drawn, 0 to 1; premultiplied, so 0 leaves what is behind it untouched. */
  setFade(fade: number): void;
  /**
   * @param camera whatever camera the frame will be drawn with; its projection
   *   and orientation are what the view ray is reconstructed from.
   * @param camUp unit local zenith at the camera's ground point, in the frame
   *   the camera is posed in - the world frame above the claim, the plot's own
   *   y-up frame inside it.
   * @param camRadiusM ground radius plus the camera's altitude.
   * @param sunDir unit toward the sun, in that same frame.
   * @param irradianceWm2 the body's top-of-atmosphere solar irradiance.
   */
  update(
    camera: THREE.PerspectiveCamera,
    camUp: THREE.Vector3,
    camRadiusM: number,
    sunDir: THREE.Vector3,
    irradianceWm2: number,
  ): void;
  dispose(): void;
}

export function createAtmoSky(config: { atmo: AtmosphereHandle }): AtmoSkyHandle {
  const { atmo } = config;
  const uniforms: Record<string, THREE.IUniform> = {
    ...atmo.uniforms,
    uSkyInvProj: { value: new THREE.Matrix4() },
    uSkyCamRot: { value: new THREE.Matrix3() },
    uSkyCamUp: { value: new THREE.Vector3(0, 1, 0) },
    uSkyCamRadius: { value: 0 },
    uSkySunDir: { value: new THREE.Vector3(0, 1, 0) },
    uSkySunIrradiance: { value: 0 },
    uSkyCamFwd: { value: new THREE.Vector3(0, 0, -1) },
    uSkyDepthRange: { value: new THREE.Vector2(0.1, 1e12) },
    uSkyHueKeep: { value: 1 },
    uSkyFade: { value: 1 },
    uSkyBehind: {
      value: new THREE.Vector3(
        ((ATLAS_CLEAR_HEX >> 16) & 255) / 255, ((ATLAS_CLEAR_HEX >> 8) & 255) / 255, (ATLAS_CLEAR_HEX & 255) / 255),
    },
  };
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERT,
    fragmentShader: `${atmo.glsl}\n${FRAG}`,
    // On, against the fragment's own entry depth: a globe in front of the air
    // keeps its pixels and a globe behind it is blended over.
    depthTest: true,
    depthWrite: false,
    // Premultiplied, not additive: alpha is the air's own extinction, so the
    // stars behind a thick sky go out instead of shining through it. `transparent`
    // stays false so this is in the OPAQUE pass and every piece of terrain draws
    // over it by depth (three applies `blending` either way - WebGLState.setMaterial).
    transparent: false,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
  });
  material.name = 'atmo-sky';
  const geometry = new THREE.PlaneGeometry(2, 2);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'atmo-sky';
  mesh.frustumCulled = false;
  // After the star shell's -100000, which is what the sky's alpha is drawn
  // OVER, and after every globe the air is in front of.
  mesh.renderOrder = SKY_RENDER_ORDER;
  mesh.visible = false;

  const rot = new THREE.Matrix3();
  const fwd = new THREE.Vector3();
  return {
    mesh,
    setLegacyOrder(on: boolean): void {
      mesh.renderOrder = on ? SKY_LEGACY_RENDER_ORDER : SKY_RENDER_ORDER;
      material.depthTest = !on;
      material.needsUpdate = true;
    },
    setHueKeep(on: boolean): void {
      uniforms['uSkyHueKeep']!.value = on ? 1 : 0;
    },
    setFade(fade: number): void {
      uniforms['uSkyFade']!.value = fade;
    },
    update(camera, camUp, camRadiusM, sunDir, irradianceWm2): void {
      (uniforms['uSkyInvProj']!.value as THREE.Matrix4).copy(camera.projectionMatrixInverse);
      (uniforms['uSkyCamRot']!.value as THREE.Matrix3)
        .copy(rot.setFromMatrix4(camera.matrixWorld));
      (uniforms['uSkyCamUp']!.value as THREE.Vector3).copy(camUp).normalize();
      uniforms['uSkyCamRadius']!.value = camRadiusM;
      (uniforms['uSkySunDir']!.value as THREE.Vector3).copy(sunDir).normalize();
      uniforms['uSkySunIrradiance']!.value = irradianceWm2 * SKY_EXPOSURE;
      (uniforms['uSkyCamFwd']!.value as THREE.Vector3).copy(camera.getWorldDirection(fwd));
      (uniforms['uSkyDepthRange']!.value as THREE.Vector2).set(camera.near, camera.far);
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
    },
  };
}
